import type { IntlShape } from 'react-intl'
import { getCheckInCheckOutText } from 'components/Search/utils'
import { DMY_CASUAL_SHORT_FORMAT, ISO_DATE_FORMAT } from 'constants/dateFormats'
import { isItemCancelled } from 'lib/order/orderUtils'
import moment from 'moment'
import { deriveFlightDisplayData, formatFlightRouteLabel, formatFlightTypeLabel } from './flightDisplayUtils'
import { sumOccupancies } from './bookingOccupancyUtils'
import { formatOrderTileOccupancyLabel } from './orderTileDisplayUtils'

export interface OrderTypeSummary {
  typeId: string
  type: string
  count: number
  primaryName?: string
}

export interface OrderTileData {
  bookingType: 'order'
  itemId: string
  orderId: string
  bookingItemIds: Array<string>
  propertyName: string
  additionalBookings: number
  orderTypeIds: Array<string>
  checkInDate?: string
  imageId?: string
  checkOutDate?: string
  occupancy?: App.Occupants
  primaryFlightItemId?: string
}

interface BookingTileSource {
  itemId: string
  bookingType: 'hotel' | 'experience' | 'flight'
  propertyName: string
  checkInDate?: string
  checkOutDate?: string
  imageId?: string
  occupancy?: App.Occupants
  flightItemId?: string
}

export function getFlightBookingItemIds(tiles: Array<OrderTileData>): Array<string> {
  return tiles.flatMap(tile => tile.primaryFlightItemId ? [tile.primaryFlightItemId] : [])
}

export function getFlightDetailsStateByItemIds(
  flightDetailsState: App.State['orders']['flightDetails'],
  itemIds: Array<string>,
): App.State['orders']['flightDetails'] {
  const relevantFlightDetailsState: App.State['orders']['flightDetails'] = {}

  itemIds.forEach(itemId => {
    if (flightDetailsState[itemId]) {
      relevantFlightDetailsState[itemId] = flightDetailsState[itemId]
    }
  })

  return relevantFlightDetailsState
}

export function getSelectedBookingItemIds({
  modalAddedBookingItemIds,
  bookingDataByTag,
  selectedTagSet,
}: {
  modalAddedBookingItemIds: Array<string>
  bookingDataByTag: Map<string, OrderTileData>
  selectedTagSet: ReadonlySet<string>
}): Array<string> {
  const itemIds = new Set(modalAddedBookingItemIds)

  for (const [tag, tile] of bookingDataByTag.entries()) {
    if (selectedTagSet.has(tag)) {
      tile.bookingItemIds.forEach(itemId => itemIds.add(itemId))
    }
  }

  return [...itemIds]
}

export function getFlightBookingTag(
  intl: IntlShape,
  flightDetails?: App.FlightOrderItemDetails,
): string {
  const {
    departureDate,
    arrivalDate,
    departureLabel,
    arrivalLabel,
    isReturnFlight,
    isDirectFlight,
  } = deriveFlightDisplayData(flightDetails)

  const routeText = formatFlightRouteLabel(intl, departureLabel, arrivalLabel)
  const flightTypeText = formatFlightTypeLabel(intl, isReturnFlight, isDirectFlight)

  let dateText = ''
  if (departureDate) {
    if (isReturnFlight && arrivalDate) {
      dateText = getCheckInCheckOutText(departureDate, arrivalDate)
    } else {
      dateText = moment(departureDate).format(DMY_CASUAL_SHORT_FORMAT)
    }
  }

  const flightTagParts = [routeText, flightTypeText]
  if (dateText) {
    flightTagParts.push(dateText)
  }

  return flightTagParts.join(' · ')
}

export function getAdditionalBookings(orderTypes: Array<OrderTypeSummary>): number {
  const bookings = orderTypes
    .filter(item => item.typeId !== 'insurance' && item.typeId !== 'luxPlus')
    .reduce((total, item) => total + item.count, 0)

  return Math.max(0, bookings - 1)
}

export function getOrderTypeIds(orderTypes: Array<OrderTypeSummary>): Array<string> {
  const ignoredTypeIds = new Set(['insurance', 'luxPlus'])
  return [...new Set(
    orderTypes
      .filter(({ typeId }) => !ignoredTypeIds.has(typeId))
      .map(({ typeId }) => typeId),
  )]
}

export function getOrderTileTitle(
  intl: IntlShape,
  tile: Pick<OrderTileData, 'propertyName' | 'additionalBookings'>,
): string {
  if (tile.additionalBookings === 0) {
    return tile.propertyName
  }

  const additionalBookingsText = intl.formatMessage(
    {
      id: '4diYptumZS6tDKtvhJSB',
      defaultMessage: '+ {count} more',
      description: 'Label showing additional bookings count in order title',
    },
    { count: tile.additionalBookings },
  )

  return `${tile.propertyName} ${additionalBookingsText}`
}

export function buildBookingTag({
  intl,
  tile,
  flightDetailsState,
}: {
  intl: IntlShape
  tile: OrderTileData
  flightDetailsState: App.State['orders']['flightDetails']
}): string {
  if (tile.primaryFlightItemId) {
    return getFlightBookingTag(intl, flightDetailsState[tile.primaryFlightItemId]?.flight)
  }

  const parts: Array<string> = [getOrderTileTitle(intl, tile)]

  if (tile.checkInDate && tile.checkOutDate) {
    parts.push(getCheckInCheckOutText(tile.checkInDate, tile.checkOutDate))
  } else if (tile.checkInDate) {
    parts.push(moment(tile.checkInDate).format(DMY_CASUAL_SHORT_FORMAT))
  }

  const occupancyLabel = formatOrderTileOccupancyLabel(intl, tile.occupancy)
  if (occupancyLabel) {
    parts.push(occupancyLabel)
  }

  return parts.join(' · ')
}

export function getOrderTileDataList(
  order: App.Order,
  orderTypes: Array<OrderTypeSummary> = [],
  orderTravelDuration?: { earliest?: string, latest?: string },
): Array<OrderTileData> {
  const sourceItems: Array<BookingTileSource> = []

  for (const item of order.items) {
    if (isItemCancelled(item) || !item.reservation?.startDate || !item.offer?.property?.name) continue
    sourceItems.push({
      bookingType: 'hotel',
      itemId: item.id,
      propertyName: item.offer.property.name,
      checkInDate: item.reservation.startDate,
      imageId: item.offer.property?.roomType?.images?.[0]?.id,
      checkOutDate: item.reservation.endDate,
      occupancy: { adults: item.reservation.adults, children: item.reservation.children, infants: item.reservation.infants },
    })
  }

  for (const item of order.bedbankItems) {
    if (isItemCancelled(item) || !item.offer?.property?.name) continue
    sourceItems.push({
      bookingType: 'hotel',
      itemId: item.id,
      propertyName: item.offer.property.name,
      checkInDate: item.checkIn.format(ISO_DATE_FORMAT),
      imageId: item.offer.image?.id,
      checkOutDate: item.checkOut.format(ISO_DATE_FORMAT),
      occupancy: sumOccupancies(item.rooms.map(room => room.occupancy)),
    })
  }

  for (const item of order.experienceItems) {
    if (isItemCancelled(item) || !item.title) continue
    sourceItems.push({
      bookingType: 'experience',
      itemId: item.id,
      propertyName: item.title,
      checkInDate: item.date,
    })
  }

  if (order.hasFlight) {
    for (const item of order.flightItems) {
      if (isItemCancelled(item)) continue
      sourceItems.push({
        bookingType: 'flight',
        itemId: item.id,
        propertyName: 'Flight booking',
        flightItemId: item.id,
      })
    }
  }

  const primary = sourceItems[0]
  if (!primary) return []

  const firstType = orderTypes[0]
  const propertyName = firstType?.primaryName || primary.propertyName

  return [
    {
      bookingType: 'order',
      itemId: order.id,
      orderId: order.id,
      bookingItemIds: [...new Set(sourceItems.map(item => item.itemId))],
      propertyName,
      additionalBookings: getAdditionalBookings(orderTypes),
      orderTypeIds: getOrderTypeIds(orderTypes),
      checkInDate: orderTravelDuration?.earliest ?? primary.checkInDate,
      checkOutDate: orderTravelDuration?.latest ?? primary.checkOutDate,
      imageId: primary.imageId,
      occupancy: primary.occupancy,
      primaryFlightItemId: primary.flightItemId,
    },
  ]
}
